import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Button } from '../../components/ui/button';
import { useToast } from '../../components/ui/use-toast';
import orderService from '../../services/orderService';
import { playKitchenTone } from '../../utils/kitchenTone';
import { subscribeRealtime, fallbackPoll } from '../../lib/realtime';
import { extrasLine } from '../../lib/orderExtras';
import { promptDialog } from '../../components/ui/confirm-dialog';
import {
  ChefHat, Bell, BellOff, Clock, StickyNote, Users, RefreshCw,
  CheckCircle, Flame, X, Soup, Plus, Gift,
} from 'lucide-react';
import apiClient from '../../services/api';
import { useRestaurant } from '../../context/RestaurantContext';

// Safety-net poll; realtime pushes order changes immediately.
const POLL_MS = 15000;
const TONE_MS = 4000;

// Board buckets by order status: NEW rings until accepted, and accepting moves it to the silent queue.
const NEW_STATUSES = ['pending', 'ordered'];

function elapsedLabel(createdAt, nowTick) {
  if (!createdAt) return '';
  const mins = Math.max(0, Math.floor((nowTick - new Date(createdAt).getTime()) / 60000));
  if (mins < 60) return `${mins}m`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

// Longer-waiting orders escalate the card's urgency colour.
function ageClasses(createdAt, nowTick) {
  const mins = (nowTick - new Date(createdAt).getTime()) / 60000;
  if (mins >= 20) return 'text-red-600 font-bold';
  if (mins >= 10) return 'text-orange-500 font-semibold';
  return 'text-gray-500';
}

/* The wall clock in the board's header — a kitchen paces a service against the
   time of day. Its own component with its own tick, so the once-a-second
   update re-renders this span and not the board full of tickets. */
function WallClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  const h = now.getHours();
  return (
    <span title="The time now"
      className="inline-flex items-center gap-1.5 whitespace-nowrap text-xl font-bold tabular-nums text-foreground">
      <Clock className="h-5 w-5" />
      {h % 12 || 12}:{String(now.getMinutes()).padStart(2, '0')} {h >= 12 ? 'pm' : 'am'}
    </span>
  );
}

export default function KitchenView() {
  const { toast } = useToast();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailOrder, setDetailOrder] = useState(null);
  const [nowTick, setNowTick] = useState(Date.now());
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem('kds_sound') !== 'off');
  const [updatingId, setUpdatingId] = useState(null);
  // Open buffet refill requests from the floor, and which one is being answered.
  const [refills, setRefills] = useState([]);
  const [sendingRefill, setSendingRefill] = useState(null);
  // Dishes buffet guests have asked to be brought to their table.
  const [guestRequests, setGuestRequests] = useState([]);
  const [updatingRequest, setUpdatingRequest] = useState(null);
  const { restaurantId } = useRestaurant();
  const soundOnRef = useRef(soundOn);
  soundOnRef.current = soundOn;
  const hasNewRef = useRef(false);

  const loadOrders = useCallback(async () => {
    try {
      const data = await orderService.getActiveOrders();
      const formatted = (data || [])
        .map((o) => orderService.formatOrderForDisplay(o))
        // The kitchen board stops at READY; delivered orders belong to the floor staff's Order Management.
        .filter((o) => ['pending', 'ordered', 'confirmed', 'preparing', 'ready'].includes(o.status));
      setOrders(formatted);
    } catch (error) {
      console.error('Error loading kitchen orders:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Realtime refreshes the board the instant an order changes; polling stays as a slower safety net.
  useEffect(() => {
    loadOrders();
    const unsub = subscribeRealtime(['kitchen'], () => loadOrders(), { app: 'kitchen' });
    // Tightens to a few seconds by itself for as long as the socket is not open.
    const stop = fallbackPoll(loadOrders, POLL_MS);
    return () => { unsub(); stop(); };
  }, [loadOrders]);

  /* Buffet refills the floor has asked for and the kitchen hasn't sent, restaurant-wide, since the pass serves every counter. */
  const loadRefills = useCallback(async () => {
    if (!restaurantId) return;
    try {
      const { data } = await apiClient.get(
        `/users/restaurant/${restaurantId}/buffets/refills/open/`);
      setRefills(data?.success ? (data.results || []) : []);
    } catch {
      // A buffet-less restaurant or an older server shows nothing, rather than an error the kitchen can't act on.
      setRefills([]);
    }
  }, [restaurantId]);

  useEffect(() => {
    loadRefills();
    const unsub = subscribeRealtime(['buffet_refill'], () => loadRefills(), { app: 'kitchen' });
    const stop = fallbackPoll(loadRefills, POLL_MS);
    return () => { unsub(); stop(); };
  }, [loadRefills]);

  /* Dishes GUESTS at a buffet table asked for: not an order, but somebody is waiting at a numbered table. */
  const loadGuestRequests = useCallback(async () => {
    if (!restaurantId) return;
    try {
      const { data } = await apiClient.get(
        `/users/restaurant/${restaurantId}/kitchen/buffet-requests/`);
      setGuestRequests(data?.success ? (data.results || []) : []);
    } catch {
      setGuestRequests([]);
    }
  }, [restaurantId]);

  useEffect(() => {
    loadGuestRequests();
    const unsub = subscribeRealtime(['buffet_request'], () => loadGuestRequests(),
      { app: 'kitchen' });
    const stop = fallbackPoll(loadGuestRequests, POLL_MS);
    return () => { unsub(); stop(); };
  }, [loadGuestRequests]);

  const setRequestStatus = async (r, status) => {
    setUpdatingRequest(r.id);
    try {
      await apiClient.post(
        `/users/restaurant/${restaurantId}/kitchen/buffet-requests/${r.id}/`, { status });
      toast({
        title: status === 'SERVED' ? 'Taken to the table' : 'On it',
        description: `${r.item}${r.table_code ? ` · Table ${r.table_code}` : ''}`,
      });
      loadGuestRequests();
    } catch (e) {
      toast({
        title: 'Could not update the request',
        description: e?.response?.data?.error || e.message,
        variant: 'destructive',
      });
    } finally {
      setUpdatingRequest(null);
    }
  };

  // The kitchen answering: this is what adds the food to the counter's total.
  const sendRefill = async (r) => {
    setSendingRefill(r.id);
    try {
      await apiClient.post(
        `/users/restaurant/${restaurantId}/buffets/${r.buffet_id}/refills/${r.id}/`,
        { action: 'sent' });
      toast({ title: 'Sent to the counter', description: `${r.item} · ${r.buffet_name}` });
      loadRefills();
    } catch (e) {
      toast({
        title: 'Could not mark it sent',
        description: e?.response?.data?.error || e.message,
        variant: 'destructive',
      });
    } finally {
      setSendingRefill(null);
    }
  };

  // Tick every 15s so the elapsed-time labels stay honest
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 15000);
    return () => clearInterval(id);
  }, []);

  /* EVERY DISH IS ITS OWN TICKET. Dishes on one order are cooked and served
     separately — the biryani goes out when the biryani is ready, not when the
     kebab is — so the board's unit is the ORDER LINE, bucketed by the line's
     own status. Starting the first dish of a new order is what accepts the
     order (the server re-derives the order's status from its lines). */
  const tickets = useMemo(() => orders.flatMap((o) => (o.items || [])
    // A CANCELLED line leaves the board with the served ones: it is not
    // coming, so it is not work. It stays on the ORDER, where it reads as
    // cancelled — the board is what is still to cook, not the record.
    .filter((it) => it.status !== 'SERVED' && it.status !== 'CANCELLED')
    .map((it) => ({
      ...it,
      key: `${o.id}:${it.id}`,
      order_id: o.id,
      order_status: o.status,
      order_number: o.order_number,
      /* The table as it is NAMED on the floor plan (table_code). Every
         ticket, toast and detail row below prints this field. */
      table_number: o.table_code || '',
      customer_name: o.customer_name,
      order_notes: o.notes,
      // The line's own clock when it was coursed in later; the order's otherwise.
      since: it.fired_at || o.created_at,
      isNew: it.status === 'PENDING' && NEW_STATUSES.includes(o.status),
    }))), [orders]);

  /* Ring while anything is unclaimed, guest requests included - a waiting table is what the tone exists for. */
  const newCount = useMemo(
    () => tickets.filter((t) => t.isNew).length
      + guestRequests.filter((r) => r.status === 'NEW').length,
    [tickets, guestRequests],
  );
  hasNewRef.current = newCount > 0;

  useEffect(() => {
    const id = setInterval(() => {
      if (hasNewRef.current && soundOnRef.current) playKitchenTone();
    }, TONE_MS);
    return () => clearInterval(id);
  }, []);

  const toggleSound = () => {
    setSoundOn((prev) => {
      const next = !prev;
      localStorage.setItem('kds_sound', next ? 'on' : 'off');
      // An immediate feedback beep also unlocks audio for browsers requiring a gesture before playing sound.
      if (next) playKitchenTone();
      return next;
    });
  };

  /* Move ONE DISH along; the server re-derives the order's status from its
     lines (first dish on the fire accepts the order, last one plated shows
     the floor READY, last one picked up closes the kitchen's side). */
  const moveItem = async (t, nextStatus, label) => {
    setUpdatingId(t.key);
    try {
      await orderService.updateOrderItemStatus(t.id, nextStatus);
      toast({ title: label, description: `${t.quantity} × ${t.name} — table ${t.table_number}` });
      loadOrders();
    } catch (error) {
      toast({
        title: 'Update failed',
        description: error.message || 'Could not update the dish',
        variant: 'destructive',
      });
    } finally {
      setUpdatingId(null);
    }
  };

  /* The kitchen calling a dish off: out of prawns, or it should never have
     been sent. The LINE is cancelled, not the order — the other four dishes
     are already cooking — and the order carries who called it off, so the
     floor can tell the table instead of waiting for food that isn't coming.
     Asked for a reason, but not made to type one: a cook mid-service needs
     the dish off the board more than the office needs the words. */
  const cancelItem = async (t) => {
    const reason = await promptDialog({
      title: `Cancel ${t.name}?`,
      description: `Table ${t.table_number} · #${t.order_number}. It comes off the bill and the floor `
        + 'is told the kitchen cancelled it.',
      label: 'Reason (optional)',
      placeholder: 'e.g. out of prawns',
      confirmText: 'Cancel this dish',
      cancelText: 'Keep it',
      tone: 'destructive',
    });
    if (reason === null) return;   // dialog dismissed — nothing cancelled
    setUpdatingId(t.key);
    try {
      await orderService.updateOrderItemStatus(t.id, 'CANCELLED', {
        source: 'KITCHEN', reason,
      });
      toast({
        title: 'Dish cancelled',
        description: `${t.quantity} × ${t.name} — table ${t.table_number}. Taken off the bill.`,
      });
      loadOrders();
    } catch (error) {
      toast({
        title: 'Could not cancel it',
        description: error.message || 'The dish is still on the board',
        variant: 'destructive',
      });
    } finally {
      setUpdatingId(null);
    }
  };

  const queue = tickets.filter((t) => t.status === 'PENDING' || t.status === 'HELD');
  const inProcess = tickets.filter((t) => t.status === 'PREPARING');
  const ready = tickets.filter((t) => t.status === 'READY');

  /* Per-DISH pipeline; a HELD line has no button — it hasn't been fired. */
  const actionFor = (t) => {
    if (t.status === 'PENDING') {
      return { label: 'Start Cooking', next: 'PREPARING',
        className: `bg-amber-500 hover:bg-amber-600 text-white${t.isNew ? ' animate-pulse' : ''}` };
    }
    if (t.status === 'PREPARING') {
      // The cook's final step: plate, garnish, pass.
      return { label: 'Garnish & Finalize', next: 'READY', className: 'bg-green-600 hover:bg-green-700 text-white' };
    }
    /* READY has NO action: the lane is a passive stack of what's at the
       pass. Serving is the FLOOR's move — the tables screen marks the order
       served once the guest has the food — so a kitchen tap can never free
       a table or close an order early. */
    return null;
  };

  /* One DISH per card: the dish is the headline, the table is its address.
     Tapping it opens the whole order for context. */
  const renderCard = (t) => {
    const held = t.status === 'HELD';
    const action = held ? null : actionFor(t);
    const parentOrder = orders.find((o) => o.id === t.order_id);
    return (
      <div
        key={t.key}
        onClick={() => parentOrder && setDetailOrder(parentOrder)}
        className={`rounded-lg border-2 bg-white shadow-sm cursor-pointer transition-all hover:shadow-md ${
          t.isNew ? 'border-blue-500 ring-2 ring-blue-300 ring-offset-1' : 'border-gray-200'
        } ${held ? 'opacity-60' : ''}`}
      >
        <div className="flex items-start justify-between gap-2 px-3 pt-2">
          <span className="font-bold text-lg leading-tight">
            {t.quantity} × {t.name}
            {t.course && (
              <span className="ml-1.5 align-middle text-[10px] font-bold uppercase text-indigo-500">{t.course}</span>
            )}
          </span>
          <span className={`flex shrink-0 items-center gap-1 text-sm ${ageClasses(t.since, nowTick)}`}>
            <Clock className="h-3.5 w-3.5" /> {elapsedLabel(t.since, nowTick)}
          </span>
        </div>
        <div className="px-3 text-sm text-gray-600 flex items-center gap-3">
          <span>Table {t.table_number}</span>
          <span className="text-gray-400">#{t.order_number}</span>
          {t.customer_name && (
            <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" />{t.customer_name}</span>
          )}
          {held && (
            <span className="shrink-0 text-[10px] font-bold uppercase bg-gray-100 text-gray-500 rounded-full px-1.5 py-0.5">
              On hold — not fired yet
            </span>
          )}
        </div>
        {/* WHAT ELSE GOES ON THE PLATE — paid extras, then comes-with items.
            The kitchen plates these; a card naming only the dish sends out a
            bare kebab the guest has paid three portions of chips for. Blue and
            green rather than the notes' amber: a note is read, these are
            cooked, and the two must not blur at a glance across the pass. */}
        {extrasLine(t.addons) && (
          <div className="mx-3 mt-1 px-2 py-1 rounded bg-blue-50 border border-blue-200 text-blue-800 text-xs font-semibold flex items-start gap-1">
            <Plus className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span>{extrasLine(t.addons)}</span>
          </div>
        )}
        {extrasLine(t.complimentary) && (
          <div className="mx-3 mt-1 px-2 py-1 rounded bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs flex items-start gap-1">
            <Gift className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span>{extrasLine(t.complimentary)}</span>
          </div>
        )}
        {/* The line's own instruction, then the order's note — "more spicy" was
            said about the food, and the food is now several cards. */}
        {t.notes && (
          <div className="mx-3 mt-1 px-2 py-1 rounded bg-amber-100 border border-amber-300 text-amber-900 text-xs font-semibold flex items-start gap-1">
            <StickyNote className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span className="line-clamp-2">{t.notes}</span>
          </div>
        )}
        {t.order_notes && (
          <div className="mx-3 mt-1 px-2 py-1 rounded bg-amber-50 border border-amber-200 text-amber-800 text-xs flex items-start gap-1">
            <StickyNote className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span className="line-clamp-2">{t.order_notes}</span>
          </div>
        )}
        {action ? (
          <div className="px-3 pb-3 pt-2 space-y-1.5">
            <Button
              size="sm"
              disabled={updatingId === t.key}
              className={`w-full ${action.className}`}
              onClick={(e) => { e.stopPropagation(); moveItem(t, action.next, action.label); }}
            >
              {action.label}
            </Button>
            {/* Calling the dish off. Deliberately quiet next to the big
                coloured action: it is the rarer move, and a cook reaching for
                "Start cooking" at speed must not hit it by accident. */}
            <Button
              size="sm"
              variant="ghost"
              disabled={updatingId === t.key}
              className="w-full h-7 text-xs text-gray-500 hover:text-red-600 hover:bg-red-50"
              onClick={(e) => { e.stopPropagation(); cancelItem(t); }}
            >
              <X className="h-3.5 w-3.5 mr-1" /> Cancel dish
            </Button>
          </div>
        ) : <div className="pb-2" />}
      </div>
    );
  };

  const renderColumn = (title, icon, list, accentClass, emptyText) => (
    <div className="flex-1 min-w-[260px] flex flex-col bg-gray-50 rounded-xl border border-gray-200 overflow-hidden">
      <div className={`px-4 py-2.5 flex items-center justify-between ${accentClass}`}>
        <span className="flex items-center gap-2 font-semibold text-white">{icon}{title}</span>
        <span className="bg-white/25 text-white text-sm font-bold rounded-full px-2.5 py-0.5">{list.length}</span>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {list.length === 0 ? (
          <p className="text-sm text-gray-400 text-center pt-6">{emptyText}</p>
        ) : list.map(renderCard)}
      </div>
    </div>
  );

  return (
    <div className="p-4 h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h1 className="font-display text-3xl font-semibold text-foreground tracking-tight leading-none flex items-center gap-2">
          <ChefHat className="h-7 w-7" /> Kitchen View
          {newCount > 0 && (
            <span className="ml-2 bg-blue-600 text-white text-sm font-bold rounded-full px-3 py-1 animate-pulse">
              {newCount} new dish{newCount > 1 ? 'es' : ''}
            </span>
          )}
        </h1>
        <div className="flex items-center gap-3">
          <WallClock />
          <Button variant="outline" size="sm" onClick={loadOrders}>
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button
            variant={soundOn ? 'default' : 'outline'}
            size="sm"
            onClick={toggleSound}
            title={soundOn ? 'New-order tone is ON' : 'New-order tone is OFF'}
          >
            {soundOn ? <Bell className="h-4 w-4 mr-1" /> : <BellOff className="h-4 w-4 mr-1" />}
            {soundOn ? 'Sound on' : 'Sound off'}
          </Button>
        </div>
      </div>

      {/* Buffet guest requests at the top of the board: somebody is seated, waiting, and there is a table to deliver to. */}
      {guestRequests.some((r) => r.status !== 'SERVED') && (
        <div className="mb-3 rounded-xl border border-rose-300 bg-rose-50 px-3 py-2">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-rose-800">
            <Soup className="h-4 w-4" />
            Buffet guests waiting
            <span className="rounded-full bg-rose-200 px-2 py-0.5 text-[11px]">
              {guestRequests.filter((r) => r.status !== 'SERVED').length}
            </span>
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {guestRequests.filter((r) => r.status !== 'SERVED').map((r) => (
              <div key={r.id}
                className="flex items-center gap-3 rounded-lg border border-rose-200 bg-white px-3 py-2">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-gray-900">
                    {r.qty > 1 ? `${r.qty}× ` : ''}{r.item}
                    {/* The table is the point of this card: a dish with no destination is a refill, not a guest request. */}
                    {r.table_code && (
                      <span className="ml-2 rounded bg-rose-100 px-1.5 py-0.5 text-[11px] font-bold text-rose-800">
                        Table {r.table_code}
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-gray-500">
                    {[r.buffet_name, r.guest_name,
                      `${elapsedLabel(r.requested_at, nowTick)} ago`]
                      .filter(Boolean).join(' · ')}
                    {r.note ? ` · “${r.note}”` : ''}
                  </div>
                </div>
                {r.status === 'NEW' && (
                  <Button size="sm" variant="outline" disabled={updatingRequest === r.id}
                    onClick={() => setRequestStatus(r, 'PREPARING')}>
                    On it
                  </Button>
                )}
                <Button size="sm" disabled={updatingRequest === r.id}
                  onClick={() => setRequestStatus(r, 'SERVED')}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white">
                  {updatingRequest === r.id ? 'Saving…' : 'Served'}
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Buffet counters asking for more, above the lanes, since an empty counter beats any single plated order. */}
      {refills.length > 0 && (
        <div className="mb-3 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-amber-800">
            <Soup className="h-4 w-4" />
            Buffet counter needs more
            <span className="rounded-full bg-amber-200 px-2 py-0.5 text-[11px]">{refills.length}</span>
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {refills.map((r) => (
              <div key={r.id}
                className="flex items-center gap-3 rounded-lg border border-amber-200 bg-white px-3 py-2">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-gray-900">
                    {r.item}
                    {r.qty ? <span className="ml-1 font-normal text-gray-500">
                      · {r.unit === 'pcs' ? `${Math.round(r.qty)} pcs` : `${r.qty} kg`}
                    </span> : null}
                  </div>
                  <div className="text-[11px] text-gray-500">
                    {r.buffet_name}
                    {r.requested_by_name ? ` · ${r.requested_by_name}` : ''}
                    {' · '}
                    {elapsedLabel(r.requested_at, nowTick)} ago
                  </div>
                </div>
                <Button size="sm" disabled={sendingRefill === r.id}
                  onClick={() => sendRefill(r)}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white">
                  {sendingRefill === r.id ? 'Sending…' : 'Sent'}
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Three-lane board */}
      <div className="flex gap-4 flex-1 min-h-0 overflow-x-auto">
        {renderColumn('Dishes in Queue', <Bell className="h-4 w-4" />, queue, 'bg-blue-600', 'Nothing waiting')}
        {renderColumn('In Process', <Flame className="h-4 w-4" />, inProcess, 'bg-amber-500', 'Nothing on the fire')}
        {renderColumn('Ready to Serve', <CheckCircle className="h-4 w-4" />, ready, 'bg-green-600', 'Nothing ready for pickup')}
      </div>

      {/* Order detail modal */}
      {detailOrder && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setDetailOrder(null)}
        >
          <div
            className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b sticky top-0 bg-white rounded-t-xl">
              <h2 className="text-lg font-bold">Order #{detailOrder.order_number}</h2>
              <button onClick={() => setDetailOrder(null)} className="p-1 rounded hover:bg-gray-100">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="px-5 py-4 space-y-4">
              <div className="grid grid-cols-2 gap-2 text-sm">
                {/* The raw order, not the mapped ticket, so the floor-plan
                    name has to be resolved again here. */}
                <p><strong>Table:</strong> {detailOrder.table_code || '—'}</p>
                <p><strong>Status:</strong> <span className="uppercase">{detailOrder.status}</span></p>
                {detailOrder.customer_name && <p><strong>Customer:</strong> {detailOrder.customer_name}</p>}
                <p><strong>Waiting:</strong> {elapsedLabel(detailOrder.created_at, nowTick)}</p>
              </div>

              {detailOrder.notes && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-md text-sm">
                  <p className="font-semibold text-amber-800 flex items-center gap-1 mb-1">
                    <StickyNote className="h-4 w-4" /> Order note
                  </p>
                  <p className="text-amber-900">{detailOrder.notes}</p>
                </div>
              )}

              <div>
                <h3 className="font-semibold mb-2">Items</h3>
                <ul className="divide-y border rounded-md">
                  {detailOrder.items.map((item) => (
                    <li key={item.id} className="px-3 py-2 text-sm">
                      <div className="flex justify-between font-medium">
                        <span className={item.status === 'CANCELLED' ? 'line-through text-gray-400' : ''}>
                          {item.quantity} × {item.name}
                          {item.course && (
                            <span className="ml-1.5 text-[10px] font-bold uppercase text-indigo-500">{item.course}</span>
                          )}
                        </span>
                        <span className={`text-xs uppercase ${
                          item.status === 'HELD' ? 'text-gray-400 font-bold'
                            : item.status === 'CANCELLED' ? 'text-red-500 font-bold' : 'text-gray-500'
                        }`}>
                          {item.status === 'HELD' ? 'On hold — not fired yet' : item.status}
                        </span>
                      </div>
                      {/* Why the dish is struck through, in the kitchen's own
                          words where it gave any — the cook who opens this
                          order next is the one who needs to know. */}
                      {item.status === 'CANCELLED' && (
                        <p className="mt-1 text-xs text-red-600">
                          {{ KITCHEN: 'Cancelled by kitchen', FLOOR: 'Cancelled by floor', COUNTER: 'Cancelled at counter' }[item.cancelled_by] || 'Cancelled'}
                          {item.cancel_reason ? ` — ${item.cancel_reason}` : ''}
                        </p>
                      )}
                      {extrasLine(item.addons) && (
                        <p className="mt-1 text-blue-700">+ {extrasLine(item.addons)}</p>
                      )}
                      {extrasLine(item.complimentary) && (
                        <p className="mt-1 text-emerald-700">
                          Comes with {extrasLine(item.complimentary)}
                        </p>
                      )}
                      {item.notes && (
                        <p className="mt-1 text-amber-700 bg-amber-50 border border-amber-100 rounded px-2 py-1">
                          {item.notes}
                        </p>
                      )}
                    </li>
                  ))}
                </ul>
              </div>

              {/* No order-level button: dishes move one by one on their own
                  cards, and the order's status follows them on the server. */}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
